import React, { useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useIsPremiumJSW } from '@jira/navigation-apps__atlassian-navigation/src/controllers/edition/index.tsx';
import { MENU_ID_PLANS } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { useSidebarNav4 } from '@jira/navigation-apps__sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import { useNavigationPlansVisibility } from '@jira/portfolio__services-plans-item-visibility/src/useNavigationViewPlansPermission.tsx';

import type { Nav4DragAndDropMenuItemProps } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { Nav4Plans$key } from './__generated__/Nav4Plans.graphql';
import type { Nav4PlansForLanding$key } from './for-landing/__generated__/Nav4PlansForLanding.graphql';
import { Nav4PlansActionButtons } from './common/action-buttons/Nav4PlansActionButtons.tsx';
import { Nav4PlansDefault } from './default/Nav4PlansDefault.tsx';
import { Nav4PlansForLanding } from './for-landing/Nav4PlansForLanding.tsx';
import { Nav4PlansPremiumUpsellFeatureGate } from './premium-upsell-feature-gate/Nav4PlansPremiumUpsellFeatureGate.tsx';

export type Nav4PlansProps = Nav4DragAndDropMenuItemProps & {
	isExpanded: boolean;
	queryRef: Nav4Plans$key;
};

export function Nav4Plans(props: Nav4PlansProps): React.JSX.Element | null {
	const { isExpanded, queryRef, ...dndProps } = props;
	// eslint-disable-next-line @atlassian/relay/use-fragment-naming, @atlassian/relay/use-fragment-naming
	const data = useFragment<Nav4Plans$key>(
		graphql`
			fragment Nav4Plans on Query {
				...Nav4PlansForLanding @dangerously_unaliased_fixme @include(if: $isPlansExpanded)
				jira @optIn(to: "JiraAdvancedRoadmapsNavigation") {
					hasIsPlansExpanded: __typename @include(if: $isPlansExpanded)
					advancedRoadmapsNavigation(cloudId: $cloudId) {
						hasEditPermissions
						hasCreateSamplePlanPermissions
						hasGlobalPlansAdminPermissions
					}
				}
			}
		`,
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming
		queryRef,
	);
	const hasEditPermissions = data.jira?.advancedRoadmapsNavigation?.hasEditPermissions ?? false;
	const hasCreateSamplePlanPermissions =
		data.jira?.advancedRoadmapsNavigation?.hasCreateSamplePlanPermissions ?? false;
	const hasGlobalPlansAdminPermissions =
		data.jira?.advancedRoadmapsNavigation?.hasGlobalPlansAdminPermissions ?? false;
	const actionButtons = useMemo(
		() => (
			<Nav4PlansActionButtons
				hasEditPermissions={hasEditPermissions}
				hasCreateSamplePlanPermissions={hasCreateSamplePlanPermissions}
				hasGlobalPlansAdminPermissions={hasGlobalPlansAdminPermissions}
			/>
		),
		[hasEditPermissions, hasCreateSamplePlanPermissions, hasGlobalPlansAdminPermissions],
	);
	const [shouldRender] = useNavigationPlansVisibility();
	const isPremium = useIsPremiumJSW();

	if (!shouldRender) {
		return null;
	}

	if (!isPremium) {
		return <Nav4PlansPremiumUpsellFeatureGate {...dndProps} />;
	}

	return (
		<Nav4PlansComponent
			actionButtons={actionButtons}
			data={data}
			isExpanded={isExpanded}
			hasFragment={data.jira?.hasIsPlansExpanded === 'JiraQuery'}
			{...dndProps}
		/>
	);
}

type Nav4PlansComponentProps = Nav4DragAndDropMenuItemProps & {
	actionButtons: React.JSX.Element;
	data: Nav4PlansForLanding$key;
	hasFragment?: boolean;
	isExpanded: boolean;
};

const Nav4PlansComponent = (props: Nav4PlansComponentProps) => {
	const { actionButtons, isExpanded, data, hasFragment, ...dndProps } = props;
	const { isInitialSelectedPath } = useSidebarNav4();
	const isExpandedValue = fg('navx-global-side-nav')
		? !!dndProps.isExpandOrFlyoutOpen && isExpanded
		: isExpanded;

	return isInitialSelectedPath(MENU_ID_PLANS) && hasFragment ? (
		<Nav4PlansForLanding
			actionsOnHover={actionButtons}
			isExpanded={isExpandedValue}
			queryRef={data}
			{...dndProps}
		/>
	) : (
		<Nav4PlansDefault actionsOnHover={actionButtons} isExpanded={isExpandedValue} {...dndProps} />
	);
};
